import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Grid, Paper, Button, Container, IconButton } from '@material-ui/core';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Title from 'components/Title';
import { useConfirm, useQuery } from 'hooks';
import { useStyles, tableStyles } from './List.styled';
import { Table } from 'components/UI';
import { toQueryString } from 'utils';
import Layout from 'components/Layout';
import EditIcon from '@material-ui/icons/Edit';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import { withStyles } from '@material-ui/core/styles';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { PricingActions } from 'state/modules/pricing';
import InputAdornment from '@material-ui/core/InputAdornment';
import { truncateKeywordSpaces } from 'utils/utils';
import { debounce } from 'lodash';

const TooltipStyled = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.tooltip.background,
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(12),
  },
}))(Tooltip);

const RulesetList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [groupsCount] = useState(5);
  const [currentRuleset, setCurrentRuleset] = useState(null);
  const history = useHistory();
  const urlParams = useQuery();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const rulesets = useSelector((state) => state.pricing.rulesets);
  const pagination = useSelector((state) => state.pricing.pagination);
  const fetchingRulesets = useSelector((state) => state.pricing.fetchingRulesets);
  const currentOrganization = useSelector((state) => state.auth?.currentOrganization);
  const isOwner = currentOrganization?.organization_type === 'OWNER';

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  /**
   * Get vehicles grpups list
   * @param {object} params Request params
   */
  const fetchRulesets = useCallback(
    (params = {}) => {
      const _params = { ...params, group_type: 'VEHICLE' };
      if (_params?.keyword) {
        _params.keyword = truncateKeywordSpaces(_params.keyword);
      }
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(PricingActions.fetchRulesets(_params, { signal: abortController?.current?.signal }));
    },
    [dispatch]
  );

  const fetchRulesetsDebounced = useCallback(
    debounce((params) => fetchRulesets(params), 1000),
    [fetchRulesets]
  );

  const [toggleDeleteDocument, DeleteRulesetConfirm] = useConfirm({
    title: i18n.t('ruleset.delete_ruleset_title'),
    message: i18n.t('ruleset.delete_ruleset_description'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: useCallback(
      () =>
        dispatch(
          PricingActions.fetchDeleteRuleset(
            currentRuleset,
            { archived: true },
            {
              action: fetchRulesets,
              params: { ...params, page: 1 },
            }
          )
        ),
      [dispatch, currentRuleset, fetchRulesets, params]
    ),
  });

  /**
   * Handla delete ruleset
   * @param {object} row Ruleset data
   */
  const handeDelete = (row) => {
    toggleDeleteDocument();
    setCurrentRuleset(row.id);
  };

  /**
   * Handla edit ruleset
   * @param {object} row Ruleset data
   */
  const handleEdit = (row) => {
    dispatch(PricingActions.resetCurrentRuleset());
    dispatch(PricingActions.resetOnboarding());
    history.push(buildUrl(`/pricing-rulesets/${row?.id}/update`));
  };

  /**
   * Handla clone ruleset
   * @param {object} row Ruleset data
   */
  const handleClone = (row) => {
    dispatch(PricingActions.resetCurrentRuleset());
    dispatch(PricingActions.resetOnboarding());
    history.push(buildUrl(`/pricing-rulesets/${row?.id}/clone`));
  };

  /**
   * Table columns
   * Show or hide columns with omit key
   */
  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      grow: 0,
      minWidth: '4rem',
    },
    {
      name: i18n.t('ruleset.ruleset_name'),
      selector: 'name',
      grow: 2,
    },
    {
      name: isOwner ? i18n.t('ruleset.active_fleets') : i18n.t('ruleset.active_vehicle_groups'),
      selector: 'active_groups',
      cell: (row) => {
        return (
          <Box
            display="flex"
            flexWrap="wrap"
            onClick={() => handleRowClick(row)}
            className={classes.cursorDefault}
          >
            {row?.active_groups?.length > 0 ? (
              <>
                {row?.active_groups.map((group, index) => {
                  if (index + 1 > groupsCount) return null;
                  return (
                    <Box key={`acitve-group-${index}`} fontSize={13} mr={0.25}>
                      {group.name}
                      {index !== groupsCount && index + 1 !== row?.active_groups.length ? '; ' : ''}
                    </Box>
                  );
                })}
                {row.active_groups.length > groupsCount && (
                  <TooltipStyled
                    placement="right"
                    title={
                      <>
                        {row?.active_groups.map((group, index) => {
                          if (index + 1 <= groupsCount) return null;
                          return (
                            <div key={`container-group-${index}`}>
                              {' '}
                              {group.name}
                              {index + 1 !== row?.active_groups?.length ? ';' : ''}
                            </div>
                          );
                        })}
                      </>
                    }
                  >
                    <div className={classes.seeMore}>
                      <Box fontSize={13} ml={0.25}>
                        (+{row.active_groups.length - groupsCount})
                      </Box>
                    </div>
                  </TooltipStyled>
                )}
              </>
            ) : (
              '--'
            )}
          </Box>
        );
      },
      grow: 4,
    },
    {
      name: i18n.t('common.actions'),
      selector: 'action',
      minWidth: '9rem',
      cell: (row) => {
        return (
          <Box display="flex" justifyContent="center">
            <Tooltip title={i18n.t('ruleset.edit_ruleset')}>
              <IconButton aria-label="edit" size="medium" onClick={() => handleEdit(row)}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title={i18n.t('ruleset.clone_ruleset')}>
              <IconButton aria-label="clone" size="medium" onClick={() => handleClone(row)}>
                <FileCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title={i18n.t('ruleset.delete_ruleset')}>
              <IconButton aria-label="delete" size="medium" onClick={() => handeDelete(row)}>
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
      grow: 0,
      center: true,
      button: true,
    },
  ];

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchRulesetsDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchRulesetsDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchRulesets(params);
  }, []);

  const handleParams = (params) => {
    setParams(params);
    const _params = toQueryString(params);
    history.replace(buildUrl(`/pricing-rulesets${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column }) => {
    const newParams = {
      ...params,
      sort_by: column.sortableField,
    };
    const _params = toQueryString(newParams);
    handleParams(_params);
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/pricing-rulesets/${row?.id}`));
  };

  /**
   * Handle search filter
   * @param {string} name Name of the field to search
   * @param {string} value Value of the input
   */
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => (!filters[key] || filters[key] === '') && delete filters[key]
    );
    handleParams(filters);
  };

  return (
    <Layout title={i18n.t('ruleset.pricing')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Title title={i18n.t('ruleset.pricing')} />
              </Grid>
              <Grid item>
                <Button
                  onClick={() => {
                    dispatch(PricingActions.resetCurrentRuleset());
                    dispatch(PricingActions.resetOnboarding());
                    history.push(buildUrl('/pricing-rulesets/create'));
                  }}
                  style={{ textTransform: 'capitalize' }}
                  variant="contained"
                  color="primary"
                >
                  {i18n.t('common.create_ruleset')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3}>
            <SearchField
              label={i18n.t('ruleset.search')}
              field={{
                onChange: search,
                name: 'keyword',
              }}
              inputProps={{
                startAdornment: (
                  <InputAdornment
                    classes={{
                      root: classes.searchIcon,
                    }}
                    position="start"
                  >
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={rulesets}
                columns={columns}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                loading={fetchingRulesets}
                extraStyles={{ ...tableStyles }}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
              />
            </Paper>
          </Grid>
        </Grid>
        <DeleteRulesetConfirm />
      </Container>
    </Layout>
  );
};

export default RulesetList;
